import React, { useState, useRef } from 'react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Textarea } from '@/Components/ui/textarea';
import { useCreateSupportTicketMutation, useGetSupportTicketsQuery } from '../services/supportTicketApiSlice';
import { toast } from 'sonner';
import { FormLoader } from '@/Components/loader';
import Loader from '@/Components/loader';
import { Upload, X, FileImage, MessageSquare, Plus, List, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import moment from 'moment';

function Support() {
    const [activeTab, setActiveTab] = useState('create'); // 'create' or 'view'
    const [subject, setSubject] = useState('');
    const [details, setDetails] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [attachmentPreview, setAttachmentPreview] = useState(null);
    const fileInputRef = useRef(null);

    const [createTicket, { isLoading: isCreating }] = useCreateSupportTicketMutation();
    const { data: ticketsData, isLoading: isLoadingTickets, refetch } = useGetSupportTicketsQuery();

    // Extract tickets from the API response structure
    // Response structure: { data: { tickets: [...], pagination: {...} } }
    const tickets = Array.isArray(ticketsData?.data?.tickets)
        ? ticketsData.data.tickets
        : [];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
            toast.error('Invalid file type. Please upload JPEG, JPG, or PNG only.');
            return;
        }

        // Validate file size (2MB = 2 * 1024 * 1024 bytes)
        const maxSize = 2 * 1024 * 1024; // 2MB in bytes
        if (file.size > maxSize) {
            toast.error('File size exceeds 2MB limit. Please choose a smaller file.');
            return;
        }

        setAttachment(file);

        // Create preview for image
        const reader = new FileReader();
        reader.onloadend = () => {
            setAttachmentPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveAttachment = () => {
        setAttachment(null);
        setAttachmentPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!subject.trim()) {
            toast.error('Please enter a subject');
            return;
        }

        if (!details.trim()) {
            toast.error('Please enter ticket details');
            return;
        }

        try {
            let result;

            if (attachment) {
                // Create FormData for request with attachment
                const formData = new FormData();
                formData.append('subject', subject);
                formData.append('details', details);
                formData.append('attachment', attachment);

                result = await createTicket(formData).unwrap();
            } else {
                // Regular JSON request without attachment
                result = await createTicket({
                    subject: subject.trim(),
                    details: details.trim(),
                }).unwrap();
            }

            // Show success toast with ticket ID
            if (result?.data?.ticket_id) {
                toast.success(
                    <div>
                        <p className="font-semibold">{result.message || 'Ticket created successfully'}</p>
                        <p className="text-sm mt-1">Ticket ID: {result.data.ticket_id}</p>
                    </div>,
                    { duration: 5000 }
                );
            } else {
                toast.success(result?.message || 'Ticket created successfully');
            }

            // Reset form
            setSubject('');
            setDetails('');
            setAttachment(null);
            setAttachmentPreview(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Switch to view tab and refetch tickets
            setActiveTab('view');
            refetch();
        } catch (error) {
            toast.error(
                error?.data?.message ||
                error?.message ||
                'Failed to create ticket. Please try again.'
            );
        }
    };

    const getStatusBadge = (status) => {
        const statusLower = status?.toLowerCase();

        // Format status text for display
        const formatStatus = (status) => {
            if (!status) return 'Open';
            const lower = status.toLowerCase();
            if (lower === 'in_progress' || lower === 'in progress') {
                return 'In Progress';
            }
            // Capitalize first letter of each word
            return status.split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ');
        };

        if (statusLower === 'resolved' || statusLower === 'closed') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle2 className="w-3 h-3" />
                    {formatStatus(status)}
                </span>
            );
        } else if (statusLower === 'in progress' || statusLower === 'in_progress' || statusLower === 'pending') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Clock className="w-3 h-3" />
                    {formatStatus(status)}
                </span>
            );
        } else {
            // Open status - red
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <AlertCircle className="w-3 h-3" />
                    {formatStatus(status) || 'Open'}
                </span>
            );
        }
    };

    return (
        <div className="min-h-screen bg-[#F6F8FA] p-4 laptop:p-8">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl laptop:text-3xl font-bold text-[#002C3C] mb-2">
                        Support Center
                    </h1>
                    <p className="text-[#525866] text-sm laptop:text-base">
                        Create a new support ticket or view your existing tickets
                    </p>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-lg shadow-sm border border-[#E2E4E9] mb-6">
                    <div className="flex border-b border-[#E2E4E9]">
                        <button
                            onClick={() => setActiveTab('create')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-all ${activeTab === 'create'
                                ? 'text-[#1A6A73] border-b-2 border-[#1A6A73] bg-[#F6F8FA]'
                                : 'text-[#525866] hover:text-[#1A6A73] hover:bg-[#F6F8FA]'
                                }`}
                        >
                            <div className="flex items-center justify-center gap-2">
                                <Plus className="w-4 h-4" />
                                Create Ticket
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('view')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-all ${activeTab === 'view'
                                ? 'text-[#1A6A73] border-b-2 border-[#1A6A73] bg-[#F6F8FA]'
                                : 'text-[#525866] hover:text-[#1A6A73] hover:bg-[#F6F8FA]'
                                }`}
                        >
                            <div className="flex items-center justify-center gap-2">
                                <List className="w-4 h-4" />
                                View Tickets
                                {tickets.length > 0 && (
                                    <span className="ml-1 px-2 py-0.5 bg-[#1A6A73] text-white text-xs rounded-full">
                                        {tickets.length}
                                    </span>
                                )}
                            </div>
                        </button>
                    </div>
                </div>

                {/* Content */}
                {activeTab === 'create' ? (
                    <div className="bg-white rounded-lg shadow-sm border border-[#E2E4E9] p-6 laptop:p-8">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Subject */}
                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-[#002C3C] mb-2">
                                    Subject <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    id="subject"
                                    type="text"
                                    placeholder="e.g., Issue with Session"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="w-full"
                                    required
                                />
                            </div>

                            {/* Details */}
                            <div>
                                <label htmlFor="details" className="block text-sm font-medium text-[#002C3C] mb-2">
                                    Details <span className="text-red-500">*</span>
                                </label>
                                <Textarea
                                    id="details"
                                    placeholder="Describe your issue in detail..."
                                    value={details}
                                    onChange={(e) => setDetails(e.target.value)}
                                    className="w-full min-h-[120px]"
                                    required
                                />
                            </div>

                            {/* Attachment */}
                            <div>
                                <label className="block text-sm font-medium text-[#002C3C] mb-2">
                                    Attachment (Optional)
                                </label>
                                <div className="space-y-3">
                                    {!attachment ? (
                                        <div className="border-2 border-dashed border-[#E2E4E9] rounded-lg p-6 text-center hover:border-[#1A6A73] transition-colors">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/jpeg,image/jpg,image/png"
                                                onChange={handleFileChange}
                                                className="hidden"
                                                id="file-upload"
                                            />
                                            <label
                                                htmlFor="file-upload"
                                                className="cursor-pointer flex flex-col items-center gap-2"
                                            >
                                                <Upload className="w-8 h-8 text-[#525866]" />
                                                <div>
                                                    <span className="text-sm font-medium text-[#1A6A73]">
                                                        Click to upload
                                                    </span>
                                                    <p className="text-xs text-[#525866] mt-1">
                                                        JPEG, JPG, PNG (Max: 2MB)
                                                    </p>
                                                </div>
                                            </label>
                                        </div>
                                    ) : (
                                        <div className="border border-[#E2E4E9] rounded-lg p-4 bg-[#F6F8FA]">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    {attachmentPreview ? (
                                                        <img
                                                            src={attachmentPreview}
                                                            alt="Preview"
                                                            className="w-16 h-16 object-cover rounded"
                                                        />
                                                    ) : (
                                                        <FileImage className="w-8 h-8 text-[#525866]" />
                                                    )}
                                                    <div>
                                                        <p className="text-sm font-medium text-[#002C3C]">
                                                            {attachment.name}
                                                        </p>
                                                        <p className="text-xs text-[#525866]">
                                                            {(attachment.size / 1024 / 1024).toFixed(2)} MB
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleRemoveAttachment}
                                                    className="p-1 hover:bg-red-100 rounded transition-colors"
                                                >
                                                    <X className="w-5 h-5 text-red-600" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="flex justify-end gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setSubject('');
                                        setDetails('');
                                        setAttachment(null);
                                        setAttachmentPreview(null);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                        }
                                    }}
                                    disabled={isCreating}
                                >
                                    Clear
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isCreating}
                                    className="bg-[#1A6A73] hover:bg-[#155a63] text-white"
                                >
                                    {isCreating ? (
                                        <span className="flex items-center gap-2">
                                            <FormLoader />
                                            Creating...
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <MessageSquare className="w-4 h-4" />
                                            Create Ticket
                                        </span>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-[#E2E4E9] p-6 laptop:p-8">
                        {isLoadingTickets ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader />
                            </div>
                        ) : !Array.isArray(tickets) || tickets.length === 0 ? (
                            <div className="text-center py-12">
                                <MessageSquare className="w-16 h-16 text-[#E2E4E9] mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-[#002C3C] mb-2">
                                    No tickets yet
                                </h3>
                                <p className="text-[#525866] mb-6">
                                    You haven't created any support tickets. Create your first ticket to get started.
                                </p>
                                <Button
                                    onClick={() => setActiveTab('create')}
                                    className="bg-[#1A6A73] hover:bg-[#155a63] text-white"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create Ticket
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {Array.isArray(tickets) && tickets.length > 0 ? tickets.map((ticket) => (
                                    <div
                                        key={ticket.id || ticket.ticket_id}
                                        className="border border-[#E2E4E9] rounded-lg p-4 laptop:p-6 hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex flex-col laptop:flex-row laptop:items-start laptop:justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-start justify-between gap-4 mb-3">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-semibold text-[#002C3C] mb-1">
                                                            {ticket.subject}
                                                        </h3>
                                                        <p className="text-sm text-[#525866] mb-3 line-clamp-2">
                                                            {ticket.details_preview || ticket.details}
                                                        </p>
                                                    </div>
                                                    {getStatusBadge(ticket.status)}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-4 text-xs text-[#525866]">
                                                    {ticket.ticket_id && (
                                                        <div>
                                                            <span className="font-medium">Ticket ID:</span>{' '}
                                                            <span className="text-[#1A6A73] font-semibold">
                                                                {ticket.ticket_id}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {ticket.created_at && (
                                                        <div>
                                                            <span className="font-medium">Created:</span>{' '}
                                                            {moment(ticket.created_at).format('MMM DD, YYYY')}
                                                        </div>
                                                    )}
                                                    {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
                                                        <div>
                                                            <span className="font-medium">Updated:</span>{' '}
                                                            {moment(ticket.updated_at).format('MMM DD, YYYY')}
                                                        </div>
                                                    )}
                                                </div>
                                                {ticket.attachment && (
                                                    <div className="mt-3 flex items-center gap-2 text-sm text-[#1A6A73]">
                                                        <FileImage className="w-4 h-4" />
                                                        <span>Attachment included</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="text-center py-12">
                                        <MessageSquare className="w-16 h-16 text-[#E2E4E9] mx-auto mb-4" />
                                        <h3 className="text-lg font-semibold text-[#002C3C] mb-2">
                                            No tickets found
                                        </h3>
                                        <p className="text-[#525866] mb-6">
                                            Unable to load tickets. Please try again.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Support;

